/**
 * PersistentStats — cross-run counters backing the achievement system.
 *
 * All fields are append-only (counters, maxes, or min-trackers). Adding new
 * achievements later just backfills progress from existing counter values
 * on first load — no destructive migration needed.
 *
 * Serialized into the save envelope under `persistentStats`. Sets are stored
 * as string arrays.
 */

export type WeaponCategory = 'ballistic' | 'energy' | 'rocket' | 'bomb' | 'melee';
export type EnemyKind = 'turret' | 'patrol' | 'charger' | 'bomber' | 'shielder';

export interface PersistentStats {
  // ── Combat ────────────────────────────────────────────
  totalKills: number;
  bestRunKills: number;
  swarmKills: number;
  swarmKillsByTier: Record<1 | 2 | 3 | 4 | 5, number>;
  bestSwarmBurst10s: number;
  killsByType: Record<EnemyKind, number>;
  bossKills: number;
  flawlessBossKills: number;
  bossSpeedrun30: number;                // 0/1 flag, incremented on first trigger
  bossPhasesCleared: number;
  bestKillBurst5s: number;
  bestNoDamageKillStreak: number;
  flawlessRun: number;                   // count of no-damage successful runs
  killsByCategory: Record<WeaponCategory, number>;
  killsByWeaponTier: Record<number, number>; // keys 1..10
  allCategoriesRun: number;              // count of runs with a kill in every category
  meleeOnlyRun: number;                  // count of melee-only successful runs
  totalDamageDealt: number;
  biggestHit: number;
  totalCrits: number;
  killsByDoT: { burn: number };
  killsWhileStunned: number;
  bestChainKill: number;
  knockbackKills: number;

  // ── Progression / Exploration ─────────────────────────
  runsCompleted: number;
  runsRecalled: number;
  perfectRunCount: number;
  bestExplorationPercent: number;        // 0..100
  bestRunXp: number;
  bestRunCoins: number;
  bestRunDuration: number;               // seconds
  bestRunDistanceFromSpawn: number;      // tiles (Chebyshev)
  tilesRevealedTotal: number;
  moneyEarnedTotal: number;
  moneySpentAtTrader: number;
  buildingsPlacedTotal: number;
  maxMapFullyExplored: number;           // w*h of largest fully-explored map
  runsToLevel10: number;                 // min-tracker; 0 = not yet reached

  // ── Loot ──────────────────────────────────────────────
  lootBasicLooted: number;
  lootUncommonLooted: number;
  lootRareLooted: number;
  lootEpicLooted: number;
  lootLegendaryLooted: number;
  lootTotalLooted: number;
  bestRunLootCount: number;
  bestRunLegendaryCount: number;
  score100Count: number;
  bossDropAtLeast95Count: number;
  weaponsUniqueDiscovered: Set<string>;
  tier10CategoriesOwnedNow: number;      // reactively recomputed; snapshot
  tier10CategoriesEverOwned: Set<WeaponCategory>;
  forgeWeaponsForged: number;
  forgeTier10Forged: number;
  forgeTier10Categories: Set<WeaponCategory>;
  moneyFromSales: number;
  bestSinglePrice: number;
  equipScore1FloorsCleared: number;
  equipSlotsFilledMax: number;           // max slots ever filled simultaneously
  equipEpicSlotsMax: number;
  equipLegendarySlotsMax: number;

  // ── Challenge / Meta / Misc ───────────────────────────
  noDamageRuns: number;
  lowHpClears: number;
  combatRecalls: number;
  fullSwarmsSurvived: number;
  fastestMapClearSec: number;            // min-tracker; 0 = not yet set
  fastBossKills: number;
  meleeOnlyRuns: number;
  soloCategoryClears: Record<WeaponCategory, number>;
  noUpgradeRuns: number;
  fiveCategoryRuns: number;
  midRunLoadoutSwaps: number;
  secondsPlayed: number;
  totalSessions: number;
  totalDeployments: number;
  totalDeaths: number;
  comebackRuns: number;
  tradesCompleted: number;
  legendaryBought: number;
  selfBombDeaths: number;
  pacifistExplorations: number;
  standStillTriggered: number;
  allStatusSimultaneous: number;
  uniquePlayDays: Set<string>;           // YYYY-MM-DD
}

export function defaultPersistentStats(): PersistentStats {
  return {
    totalKills: 0,
    bestRunKills: 0,
    swarmKills: 0,
    swarmKillsByTier: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
    bestSwarmBurst10s: 0,
    killsByType: { turret: 0, patrol: 0, charger: 0, bomber: 0, shielder: 0 },
    bossKills: 0,
    flawlessBossKills: 0,
    bossSpeedrun30: 0,
    bossPhasesCleared: 0,
    bestKillBurst5s: 0,
    bestNoDamageKillStreak: 0,
    flawlessRun: 0,
    killsByCategory: { ballistic: 0, energy: 0, rocket: 0, bomb: 0, melee: 0 },
    killsByWeaponTier: {},
    allCategoriesRun: 0,
    meleeOnlyRun: 0,
    totalDamageDealt: 0,
    biggestHit: 0,
    totalCrits: 0,
    killsByDoT: { burn: 0 },
    killsWhileStunned: 0,
    bestChainKill: 0,
    knockbackKills: 0,

    runsCompleted: 0,
    runsRecalled: 0,
    perfectRunCount: 0,
    bestExplorationPercent: 0,
    bestRunXp: 0,
    bestRunCoins: 0,
    bestRunDuration: 0,
    bestRunDistanceFromSpawn: 0,
    tilesRevealedTotal: 0,
    moneyEarnedTotal: 0,
    moneySpentAtTrader: 0,
    buildingsPlacedTotal: 0,
    maxMapFullyExplored: 0,
    runsToLevel10: 0,

    lootBasicLooted: 0,
    lootUncommonLooted: 0,
    lootRareLooted: 0,
    lootEpicLooted: 0,
    lootLegendaryLooted: 0,
    lootTotalLooted: 0,
    bestRunLootCount: 0,
    bestRunLegendaryCount: 0,
    score100Count: 0,
    bossDropAtLeast95Count: 0,
    weaponsUniqueDiscovered: new Set<string>(),
    tier10CategoriesOwnedNow: 0,
    tier10CategoriesEverOwned: new Set<WeaponCategory>(),
    forgeWeaponsForged: 0,
    forgeTier10Forged: 0,
    forgeTier10Categories: new Set<WeaponCategory>(),
    moneyFromSales: 0,
    bestSinglePrice: 0,
    equipScore1FloorsCleared: 0,
    equipSlotsFilledMax: 0,
    equipEpicSlotsMax: 0,
    equipLegendarySlotsMax: 0,

    noDamageRuns: 0,
    lowHpClears: 0,
    combatRecalls: 0,
    fullSwarmsSurvived: 0,
    fastestMapClearSec: 0,
    fastBossKills: 0,
    meleeOnlyRuns: 0,
    soloCategoryClears: { ballistic: 0, energy: 0, rocket: 0, bomb: 0, melee: 0 },
    noUpgradeRuns: 0,
    fiveCategoryRuns: 0,
    midRunLoadoutSwaps: 0,
    secondsPlayed: 0,
    totalSessions: 0,
    totalDeployments: 0,
    totalDeaths: 0,
    comebackRuns: 0,
    tradesCompleted: 0,
    legendaryBought: 0,
    selfBombDeaths: 0,
    pacifistExplorations: 0,
    standStillTriggered: 0,
    allStatusSimultaneous: 0,
    uniquePlayDays: new Set<string>(),
  };
}

export function serializeStats(s: PersistentStats): Record<string, unknown> {
  return {
    ...s,
    weaponsUniqueDiscovered: [...s.weaponsUniqueDiscovered],
    tier10CategoriesEverOwned: [...s.tier10CategoriesEverOwned],
    forgeTier10Categories: [...s.forgeTier10Categories],
    uniquePlayDays: [...s.uniquePlayDays],
  };
}

export function deserializeStats(raw: unknown): PersistentStats {
  const base = defaultPersistentStats();
  if (!raw || typeof raw !== 'object') return base;
  const o = raw as Record<string, unknown>;

  // Merge: overwrite scalar fields if present and valid; preserve defaults for
  // anything missing (supports forward-compat additions).
  const merged: PersistentStats = base;
  for (const k of Object.keys(base) as (keyof PersistentStats)[]) {
    const v = o[k as string];
    if (v === undefined) continue;
    switch (k) {
      case 'weaponsUniqueDiscovered':
      case 'tier10CategoriesEverOwned':
      case 'forgeTier10Categories':
      case 'uniquePlayDays':
        if (Array.isArray(v)) {
          (merged[k] as Set<string>) = new Set(v.filter((x): x is string => typeof x === 'string'));
        }
        break;
      case 'swarmKillsByTier':
      case 'killsByType':
      case 'killsByCategory':
      case 'killsByWeaponTier':
      case 'killsByDoT':
      case 'soloCategoryClears': {
        if (v && typeof v === 'object') {
          Object.assign(merged[k] as Record<string, number>, v as Record<string, number>);
        }
        break;
      }
      default: {
        if (typeof v === 'number' && Number.isFinite(v)) {
          (merged as unknown as Record<string, unknown>)[k as string] = v;
        }
      }
    }
  }
  return merged;
}
